/**
 * Enhanced Runtime Environment Configuration Service
 *
 * This service provides centralized access to environment variables with:
 * - Hostname-based environment detection
 * - Runtime injection support (Docker)
 * - Build-time fallbacks (Development)
 * - Environment-specific defaults
 *
 * Priority Order:
 * 1. window._env_ (Docker runtime injection) - HIGHEST PRIORITY
 * 2. Hostname-based environment config (test.ts, production.ts, etc.)
 * 3. process.env (build-time injection) - fallback for localhost
 * 4. Hardcoded defaults - LOWEST PRIORITY
 *
 * This approach allows:
 * - Same Docker image for all environments
 * - Hostname-based automatic configuration
 * - Easy local development setup
 * - 12-factor app compliance
 *
 * @module config/env
 */

import { localhostConfig, testConfig, productionConfig, EnvironmentConfig } from './environments';

/**
 * Type definition for runtime environment configuration
 */
interface RuntimeEnv {
  REACT_APP_API_BASE_URL: string;
  REACT_APP_OAUTH_CLIENT_ID: string;
  REACT_APP_OAUTH_REDIRECT_URI: string;
  REACT_APP_OAUTH_AUTH_URL: string;
  REACT_APP_OAUTH_TOKEN_URL: string;
  REACT_APP_TITLE: string;
  REACT_APP_LOGO_URL: string;
  REACT_APP_COMPANY_NAME: string;
  REACT_APP_PRIMARY_COLOR: string;
  REACT_APP_DEBUG: string;
}

/**
 * Extend Window interface to include runtime environment
 */
declare global {
  interface Window {
    _env_?: RuntimeEnv;
  }
}

/**
 * Detect current environment based on hostname
 * @returns Environment configuration object
 */
const detectEnvironment = (): EnvironmentConfig => {
  const hostname = window.location.hostname;

  // Localhost detection
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return localhostConfig;
  }

  // Test environment detection
  if (hostname.includes('test') || hostname.includes('provisioning-admin-test')) {
    return testConfig;
  }

  // Staging environment detection (add if needed)
  // if (hostname.includes('staging')) {
  //   return stagingConfig;
  // }

  // Production environment (default)
  return productionConfig;
};

/**
 * Get the environment-specific config
 */
const envConfig = detectEnvironment();

/**
 * Get environment variable value with multi-layer fallback
 * Priority: window._env_ > envConfig (hostname) > process.env > defaultValue
 *
 * @param key - Environment variable key
 * @param envConfigValue - Value from environment-specific config
 * @param defaultValue - Final fallback value
 * @returns Environment variable value
 */
const getEnvVar = (
  key: keyof RuntimeEnv,
  envConfigValue: string,
  defaultValue: string = ''
): string => {
  // Priority 1: Runtime injection (Docker)
  if (window._env_ && window._env_[key]) {
    return window._env_[key];
  }

  // Priority 2: Environment-specific config (hostname-based)
  // This takes precedence over build-time values for deployed environments
  if (envConfigValue) {
    return envConfigValue;
  }

  // Priority 3: Build-time environment (webpack) - fallback for localhost dev
  const processEnvValue = process.env[key];
  if (processEnvValue) {
    return processEnvValue;
  }

  // Priority 4: Default value
  return defaultValue;
};

/**
 * Parse boolean string value
 * @param value - String value to parse
 * @returns Boolean value
 */
const parseBoolean = (value: string | boolean): boolean => {
  if (typeof value === 'boolean') return value;
  return value?.toLowerCase() === 'true' || value === '1';
};

/**
 * Centralized configuration object
 * Uses getters for lazy evaluation - checks window._env_ at access time
 * This ensures runtime injection works even if script loads after module
 */
export const config = {
  /**
   * Environment Information
   */
  environment: {
    /** Current environment name */
    get name() { return envConfig.environment; },

    /** Current hostname */
    get hostname() { return window.location.hostname; },

    /** Configuration source */
    get source() { return window._env_ ? 'runtime' : process.env.NODE_ENV ? 'build-time' : 'hostname'; },
  },

  /**
   * API Configuration
   */
  api: {
    /** Base URL for API requests */
    get baseUrl() {
      return getEnvVar('REACT_APP_API_BASE_URL', envConfig.api.baseUrl, '/api');
    },
    /** Base URL for firmware download links (HTTP, absolute) */
    get firmwareBaseUrl() {
      return envConfig.api.firmwareBaseUrl;
    },
  },

  /**
   * OAuth2 Configuration
   */
  oauth: {
    /** OAuth2 client ID */
    get clientId() {
      return getEnvVar('REACT_APP_OAUTH_CLIENT_ID', envConfig.oauth.clientId, 'provisioning-dev');
    },

    /** OAuth2 redirect URI after authentication */
    get redirectUri() {
      return getEnvVar('REACT_APP_OAUTH_REDIRECT_URI', envConfig.oauth.redirectUri, `${window.location.origin}/auth/callback`);
    },

    /** OAuth2 authorization endpoint */
    get authUrl() {
      return getEnvVar('REACT_APP_OAUTH_AUTH_URL', envConfig.oauth.authUrl, 'https://sso.strong.blue/realms/anep-architecture/protocol/openid-connect/auth');
    },

    /** OAuth2 token endpoint */
    get tokenUrl() {
      return getEnvVar('REACT_APP_OAUTH_TOKEN_URL', envConfig.oauth.tokenUrl, 'https://sso.strong.blue/realms/anep-architecture/protocol/openid-connect/token');
    },
  },

  /**
   * UI Configuration
   */
  ui: {
    /** Application title */
    get title() {
      return getEnvVar('REACT_APP_TITLE', envConfig.ui.title, 'StrongBlue Device Provisioning');
    },

    /** Logo URL */
    get logoUrl() {
      return getEnvVar('REACT_APP_LOGO_URL', envConfig.ui.logoUrl, '/logo.jfif');
    },

    /** Company name */
    get companyName() {
      return getEnvVar('REACT_APP_COMPANY_NAME', envConfig.ui.companyName, 'Strong Blue');
    },

    /** Primary brand color */
    get primaryColor() {
      return getEnvVar('REACT_APP_PRIMARY_COLOR', envConfig.ui.primaryColor, '#215EA1');
    },
  },

  /**
   * Development Configuration
   */
  dev: {
    /** Enable debug logging and debug menu */
    get debug() {
      return parseBoolean(getEnvVar('REACT_APP_DEBUG', envConfig.dev.debug.toString(), 'false'));
    },
  },
};

/**
 * Debug utility: Log current configuration to console
 * Only logs if debug mode is enabled
 */
export const logConfig = (): void => {
  if (config.dev.debug) {
    console.group('🔧 Environment Configuration');
    console.log('Environment:', config.environment.name);
    console.log('Hostname:', config.environment.hostname);
    console.log('Config Source:', config.environment.source);
    console.log('---');
    console.log('Runtime Injection:', window._env_ ? '✅ Active' : '❌ Not Active');
    console.log('Process.env:', process.env.NODE_ENV || 'Not set');
    console.log('---');
    console.log('API Base URL:', config.api.baseUrl);
    console.log('OAuth Client ID:', config.oauth.clientId);
    console.log('OAuth Redirect URI:', config.oauth.redirectUri);
    console.log('App Title:', config.ui.title);
    console.log('Debug Mode:', config.dev.debug);
    console.groupEnd();
  }
};

// Auto-log configuration in debug mode on module load
if (config.dev.debug) {
  logConfig();
}

export default config;